import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, FileText, ArrowRight } from "lucide-react";
import BrandMark from "@/components/BrandMark";
import PriceComparisonButton from "@/components/pricing/PriceComparisonButton";

/**
 * Preview: Supply catalog v2 — SECTIONED BY PRICE TIER, full disclosure.
 * Public route: /preview/supply-tiers
 */

const CARD_BG_SUPPLY =
  "https://customer-assets-lqy194kg.emergentagent.net/job_c26c4ddf-0b99-4af9-b8f4-6f4e27981906/artifacts/0j9faakn_image.png";

const OXBLOOD = "#4A0E17";
const GOLD    = "#C9A24B";

// Mock data — grouped by price tier
const TIERS = [
  {
    price: 40,
    label: "$40 Kits",
    products: [
      { sku: "BBK-GHK-CU-50MG",   name: "GHK-Cu",  subtitle: "Copper Tripeptide",     vialMg: 50 },
      { sku: "BBK-DSIP-2MG",      name: "DSIP",    subtitle: "Delta Sleep-Inducing",  vialMg: 2  },
      { sku: "BBK-KPV-5MG",       name: "KPV",     subtitle: "α-MSH Tripeptide",       vialMg: 5  },
    ],
  },
  {
    price: 65,
    label: "$65 Kits",
    products: [
      { sku: "BBK-TB500-5MG",     name: "TB-500",  subtitle: "Thymosin β-4 Fragment",  vialMg: 5  },
      { sku: "BBK-BPC157-5MG",    name: "BPC-157", subtitle: "Body Protection Compound", vialMg: 5 },
      { sku: "BBK-GHK-CU-100MG",  name: "GHK-Cu",  subtitle: "Copper Tripeptide",     vialMg: 100 },
      { sku: "BBK-SEMAX-5MG",     name: "Semax",   subtitle: "Heptapeptide",           vialMg: 5  },
    ],
  },
  {
    price: 100,
    label: "$100 Kits",
    products: [
      { sku: "BBK-CAGRILINTIDE-5MG", name: "Cagrilintide", subtitle: "Amylin Analog",     vialMg: 5 },
      { sku: "BBK-TIRZEPATIDE-10MG", name: "Tirzepatide",  subtitle: "GIP/GLP-1 Agonist", vialMg: 10 },
    ],
  },
  {
    price: 150,
    label: "$150 Kits",
    products: [
      { sku: "BBK-KLOW-BLEND-10MG",  name: "KLOW",   subtitle: "4-Peptide Blend",    vialMg: 10 },
      { sku: "BBK-RETATRUTIDE-10MG", name: "Retatrutide", subtitle: "Triple Agonist", vialMg: 10 },
    ],
  },
];

export default function PreviewSupplyTiers() {
  return (
    <div className="min-h-screen bg-bone" data-testid="supply-tiers-page">
      {/* Site header — logo only for now, nav menu TBD */}
      <header
        className="sticky top-0 z-50 border-b bg-bone/95 backdrop-blur-sm"
        style={{ borderColor: `${OXBLOOD}18` }}
        data-testid="supply-header"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex items-center justify-between h-20">
          <BrandMark variant="dark" size="sm" />
          <span className="font-mono text-[10.5px] uppercase tracking-[0.28em]" style={{ color: OXBLOOD, opacity: 0.55 }}>
            Nav menu · TBD
          </span>
        </div>
      </header>

      <div className="max-w-[1240px] mx-auto px-6 py-14 lg:py-20">
        {/* Header */}
        <h1 className="font-serif text-5xl md:text-6xl tracking-tight mb-4" style={{ color: OXBLOOD }}>
          Research Supply. Simplified.
        </h1>
        <p className="text-[15px] text-charcoal/80 leading-relaxed max-w-2xl mb-10">
          Three-vial research kits with tier-based pricing. Every batch is
          independently third-party tested, documented, and supported by an
          available COA — giving researchers clear, batch-specific
          documentation for every product.
        </p>

        {/* How it works strip */}
        <HowItWorksStrip />

        {/* Tier sections */}
        {TIERS.map((tier) => (
          <TierSection key={tier.price} tier={tier} />
        ))}

        {/* Footer notes */}
        <div className="mt-6 border-t border-borderSubtle pt-8 max-w-2xl leading-relaxed grid gap-4 text-[13px] text-charcoal/70">
          <p className="font-mono uppercase tracking-[0.22em] text-[11px]" style={{ color: OXBLOOD }}>
            Layout notes
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Every card is 3 vials. Strength varies per peptide — visible on hover.</li>
            <li>Price is set at the section header. Each card in the section = that price.</li>
            <li>Same peptide at two strengths = two cards. See <b>GHK-Cu 50mg</b> in $40 vs <b>GHK-Cu 100mg</b> in $65.</li>
            <li>No volume discount. No free-shipping threshold. Qty is unlimited.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function HowItWorksStrip() {
  const steps = [
    { n: "01", label: "Pick a tier",        detail: "Choose the price tier that fits your research. Every product within that tier is the same price." },
    { n: "02", label: "Select your product", detail: "Choose any available 3-vial kit in that tier and add it to your cart." },
    { n: "03", label: "Check out",           detail: "Choose your shipping option, complete checkout, and your order moves to fulfillment." },
  ];
  // Sequential highlight loop: 0 → 1 → 2 → 0, ~2s per step.
  // Understated premium treatment — warmer tint + darker border + subtle glow.
  const [activeIdx, setActiveIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setActiveIdx((i) => (i + 1) % steps.length), 2000);
    return () => clearInterval(id);
  }, [steps.length]);

  return (
    <div
      className="relative rounded-lg overflow-hidden mb-14 border"
      style={{ borderColor: `${OXBLOOD}22`, background: "linear-gradient(135deg, rgba(74,14,23,0.03) 0%, rgba(201,162,75,0.05) 100%)" }}
      data-testid="how-it-works-strip"
    >
      <div className="grid grid-cols-1 md:grid-cols-3">
        {steps.map((step, i) => {
          const active = activeIdx === i;
          return (
            <div
              key={step.n}
              className="p-6 md:p-7 flex items-start gap-4 border-b md:border-b-0 md:border-r last:border-r-0 last:border-b-0"
              style={{
                borderColor: `${OXBLOOD}18`,
                background: active
                  ? "linear-gradient(135deg, rgba(201,162,75,0.10) 0%, rgba(74,14,23,0.05) 100%)"
                  : "transparent",
                boxShadow: active
                  ? `inset 0 0 0 1px ${OXBLOOD}40, 0 6px 24px -12px ${OXBLOOD}55`
                  : "inset 0 0 0 0 transparent",
                transition:
                  "background 500ms ease, box-shadow 500ms ease",
              }}
              data-testid={`how-step-${i + 1}`}
              data-active={active ? "true" : "false"}
            >
              <span
                className="font-mono text-[24px] font-bold leading-none tabular-nums"
                style={{ color: OXBLOOD, opacity: active ? 1 : 0.85, transition: "opacity 500ms ease" }}
              >
                {step.n}
              </span>
              <div>
                <p
                  className="font-sans font-bold uppercase text-[13px] tracking-[0.14em] mb-1"
                  style={{ color: OXBLOOD, fontFamily: "Manrope, ui-sans-serif, system-ui" }}
                >
                  {step.label}
                </p>
                <p className="text-[12.5px] leading-relaxed text-charcoal/75">{step.detail}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TierSection({ tier }) {
  return (
    <section className="mb-14" data-testid={`tier-section-${tier.price}`}>
      {/* Compact tier header — clinical shelf label, not a price billboard */}
      <div
        className="mb-4 rounded-md px-4 py-3"
        style={{
          background: `linear-gradient(135deg, ${OXBLOOD}0A 0%, ${GOLD}0F 100%)`,
          borderLeft: `2px solid ${OXBLOOD}66`,
        }}
      >
        <div className="flex items-baseline gap-3 flex-wrap">
          <h2
            className="font-sans font-bold text-[26px] md:text-[32px] leading-none tracking-[-0.01em]"
            style={{ color: OXBLOOD, fontFamily: "Manrope, ui-sans-serif, system-ui" }}
          >
            ${tier.price} Tier
          </h2>
          <p
            className="font-mono uppercase tracking-[0.20em] text-[11.5px] tabular-nums font-medium"
            style={{ color: OXBLOOD, opacity: 0.75 }}
          >
            {tier.products.length} available
          </p>
        </div>
        <p
          className="text-[13.5px] mt-1 tracking-[0.01em] font-medium"
          style={{ color: OXBLOOD, opacity: 0.8, fontFamily: "Manrope, ui-sans-serif, system-ui" }}
        >
          Any 3-vial kit in this tier
        </p>
      </div>

      {/* Card grid — 4 per row on desktop, cards priced identically */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
        {tier.products.map((product) => (
          <ProductCard key={product.sku} product={product} tierPrice={tier.price} />
        ))}
      </div>
    </section>
  );
}

function ProductCard({ product, tierPrice }) {
  const [hover, setHover] = useState(false);
  const { sku, name, vialMg } = product;
  return (
    <div
      className="relative aspect-square rounded-lg overflow-hidden shadow-[0_16px_40px_-16px_rgba(74,14,23,0.28)] cursor-pointer transition-[border-color] duration-300"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid={`tier-card-${sku}`}
      style={{ borderWidth: 1, borderStyle: "solid", borderColor: hover ? OXBLOOD : `${OXBLOOD}33` }}
    >
      <img src={CARD_BG_SUPPLY} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none" draggable={false} />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.75) 0%, rgba(255,255,255,0.35) 55%, rgba(255,255,255,0.15) 100%)" }}
      />
      {/* Corner crosshairs */}
      <div className="absolute inset-0 pointer-events-none">
        <span className="absolute top-2 left-2 opacity-25" style={{ color: OXBLOOD }}>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M0 5h10M5 0v10" stroke="currentColor" strokeWidth="0.75"/></svg>
        </span>
        <span className="absolute bottom-2 right-2 opacity-25" style={{ color: OXBLOOD }}>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M0 5h10M5 0v10" stroke="currentColor" strokeWidth="0.75"/></svg>
        </span>
      </div>

      {/* Wordmark */}
      <div className="absolute top-3 left-3 z-10">
        <p className="font-mono text-[11px] font-semibold leading-none tracking-[0.02em]" style={{ color: OXBLOOD }}>
          BulkBuyKits<span className="font-normal opacity-70">.com</span>
        </p>
        <div className="mt-1.5 h-[1px] w-6" style={{ background: GOLD }} />
      </div>

      {/* RUO */}
      <div className="absolute top-3 right-3 z-10">
        <span
          className="inline-flex items-center rounded-sm bg-white/85 backdrop-blur-sm font-mono uppercase tracking-[0.24em] shadow-sm text-[9px] px-1.5 py-[3px] font-medium"
          style={{ color: OXBLOOD, borderColor: `${OXBLOOD}30`, borderWidth: 1 }}
        >
          RUO
        </span>
      </div>

      {/* Content — product name + 3 stacked proof points */}
      <div
        className="absolute inset-0 flex flex-col justify-center pl-4 pr-3 pointer-events-none"
        style={{ paddingTop: 48, paddingBottom: 52, textShadow: "0 1px 2px rgba(255,255,255,0.72)" }}
      >
        <div style={{ color: OXBLOOD }}>
          <h3
            className="font-sans font-bold uppercase text-[24px] leading-[0.95] tracking-[0.01em]"
            style={{ color: OXBLOOD, fontFamily: "Manrope, ui-sans-serif, system-ui" }}
          >
            {name}
          </h3>
          {/* Proof points — three stacked, subtle bullets, medium weight */}
          <ul className="mt-3 space-y-1.5">
            {["98%+ Purity", "3rd-Party Batch Tested", "COA Available"].map((line) => (
              <li key={line} className="flex items-center gap-2">
                <span
                  className="inline-block rounded-full flex-shrink-0"
                  style={{ background: OXBLOOD, width: 5, height: 5, opacity: 0.85 }}
                  aria-hidden="true"
                />
                <span
                  className="text-[10.5px] tracking-[0.02em] font-medium"
                  style={{ color: OXBLOOD, opacity: 0.9, fontFamily: "Manrope, ui-sans-serif, system-ui" }}
                >
                  {line}
                </span>
              </li>
            ))}
          </ul>
          {/* Vial strength — visible on hover only */}
          <p
            className="font-mono uppercase tracking-[0.18em] text-[12.5px] mt-1.5 transition-opacity duration-300 font-medium"
            style={{ opacity: hover ? 1 : 0, color: OXBLOOD }}
          >
            {vialMg}mg per vial
          </p>
          {/* Compare pricing — auto-hides if no admin data exists for this SKU. */}
          <div
            className="mt-2 transition-opacity duration-300"
            style={{ opacity: hover ? 1 : 0, pointerEvents: hover ? "auto" : "none" }}
          >
            <PriceComparisonButton sku={sku} />
          </div>
        </div>
      </div>

      {/* Action row */}
      <div
        className="absolute left-3 right-3 bottom-3 z-10 flex items-stretch gap-2 transition-all duration-300"
        style={{ opacity: hover ? 1 : 0, pointerEvents: hover ? "auto" : "none", transform: hover ? "translateY(0)" : "translateY(8px)" }}
      >
        <button
          className="inline-flex items-center justify-center rounded-sm text-white font-mono uppercase tracking-[0.22em] font-semibold shadow-md text-[11px] px-4 py-2.5 gap-1.5"
          style={{ background: OXBLOOD, flex: "3 1 0%" }}
          data-testid={`quick-add-default-${sku}`}
        >
          <ShoppingBag size={13} strokeWidth={2.4} />
          Add · ${tierPrice}
        </button>
        <button
          className="inline-flex items-center justify-center rounded-sm bg-white/85 backdrop-blur-sm font-mono uppercase tracking-[0.22em] font-semibold shadow-sm text-[11px] px-2 py-2.5 gap-1.5"
          style={{ color: OXBLOOD, borderColor: `${OXBLOOD}40`, borderWidth: 1, flex: "1 1 0%" }}
          data-testid={`view-coa-${sku}`}
        >
          <FileText size={13} strokeWidth={2.4} />
          COA
        </button>
      </div>
    </div>
  );
}
